/**
 * Revise Session illustration — miniature wireframe of the revision interface:
 * accuracy hero number, focus area rows with progress bars, action button.
 */
import type { IllustrationProps } from './types';
import { usePalette } from './types';

export function ReviseSession({ variant = 'on-gradient', className, style }: IllustrationProps) {
  const p = usePalette(variant);

  return (
    <svg
      viewBox="0 0 120 140"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={style}
      aria-hidden="true"
    >
      {/* Card frame */}
      <rect
        x="4"
        y="4"
        width="112"
        height="132"
        rx="10"
        fill={p.frameFill}
        stroke={p.frameStroke}
        strokeWidth="1"
      />

      {/* Hero accuracy number — large "72%" */}
      <rect x="14" y="16" width="40" height="14" rx="3" fill={p.strong} opacity="0.18" />
      {/* Percentage accent mark */}
      <rect x="56" y="20" width="8" height="6" rx="2" fill={p.accent1} opacity="0.5" />

      {/* "accuracy" label */}
      <rect x="14" y="34" width="24" height="2.5" rx="1.25" fill={p.faint} />

      {/* Delta indicator — up arrow + number */}
      <rect x="42" y="33" width="16" height="4" rx="2" fill={p.accent1} opacity="0.3" />

      {/* Divider */}
      <rect x="14" y="46" width="92" height="0.75" rx="0.375" fill={p.cardStroke} />

      {/* "Focus areas" label */}
      <rect x="14" y="54" width="30" height="2.5" rx="1.25" fill={p.faint} />

      {/* Focus area row 1 */}
      <rect x="14" y="64" width="44" height="3" rx="1.5" fill={p.subtle} />
      <rect x="82" y="63" width="20" height="5" rx="2" fill={p.accent2} opacity="0.6" />
      {/* Progress bar */}
      <rect x="14" y="71" width="88" height="2.5" rx="1.25" fill={p.faint} />
      <rect x="14" y="71" width="37" height="2.5" rx="1.25" fill={p.accent2} opacity="0.5" />

      {/* Focus area row 2 */}
      <rect x="14" y="82" width="36" height="3" rx="1.5" fill={p.subtle} />
      <rect x="82" y="81" width="20" height="5" rx="2" fill={p.accent1} opacity="0.6" />
      {/* Progress bar */}
      <rect x="14" y="89" width="88" height="2.5" rx="1.25" fill={p.faint} />
      <rect x="14" y="89" width="48" height="2.5" rx="1.25" fill={p.accent1} opacity="0.5" />

      {/* Focus area row 3 */}
      <rect x="14" y="100" width="52" height="3" rx="1.5" fill={p.subtle} />
      <rect x="82" y="99" width="20" height="5" rx="2" fill={p.accent3} opacity="0.6" />
      {/* Progress bar */}
      <rect x="14" y="107" width="88" height="2.5" rx="1.25" fill={p.faint} />
      <rect x="14" y="107" width="54" height="2.5" rx="1.25" fill={p.accent3} opacity="0.5" />

      {/* Browse content button */}
      <rect x="14" y="120" width="92" height="10" rx="5" fill={p.buttonFill} />
      <rect x="38" y="123.5" width="44" height="3" rx="1.5" fill={p.buttonText} />
    </svg>
  );
}
